"use client";
import { useEffect, useRef, useState } from "react";
import apiClient from "@/services/api";

interface ChartData {
  month: string;
  primary: number;
  secondary: number;
}

interface AreaChartProps {
  activeChart?: string;
  dashboardStats?: any;
}

export function AreaChart({ activeChart = 'reviews', dashboardStats }: AreaChartProps) {
  const [data, setData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Generate chart data based on active chart type
  const generateChartData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = new Date().getMonth();

    const baseValue = getBaseValue();
    const chartData: ChartData[] = months.map((month, index) => {
      let primary = 0;
      let secondary = 0;

      if (index <= currentMonth) {
        const progress = (index + 1) / (currentMonth + 1);
        const variation1 = 0.7 + (Math.random() * 0.6);
        const variation2 = 0.6 + (Math.random() * 0.8);

        primary = Math.round(baseValue.primary * progress * variation1);
        secondary = Math.round(baseValue.secondary * progress * variation2);
      }

      return { month, primary, secondary };
    });

    setData(chartData);
    setLoading(false);
  };

  // Get base values based on active chart
  const getBaseValue = () => {
    switch (activeChart) {
      case 'reviews':
        return {
          primary: dashboardStats?.totalReviews || 50,
          secondary: (dashboardStats?.totalReviews || 50) * 0.7
        };
      case 'response':
        return {
          primary: dashboardStats?.responseRate || 75,
          secondary: 100 - (dashboardStats?.responseRate || 75)
        };
      case 'rating':
        return {
          primary: (dashboardStats?.avgRating || 4.2) * 20,
          secondary: (5 - (dashboardStats?.avgRating || 4.2)) * 20
        };
      case 'time':
        return {
          primary: (dashboardStats?.avgResponseTimeHours || 2) * 10,
          secondary: (dashboardStats?.avgResponseTimeHours || 2) * 15
        };
      default:
        return { primary: 100, secondary: 80 };
    }
  };

  // Get chart labels based on active chart
  const getChartLabels = () => {
    switch (activeChart) {
      case 'reviews':
        return { primary: 'Reviews Received', secondary: 'Reviews Responded', title: 'Review Activity' };
      case 'response':
        return { primary: 'Responded', secondary: 'Pending', title: 'Response Rate' };
      case 'rating':
        return { primary: 'Positive Ratings', secondary: 'Improvement Area', title: 'Rating Trends' };
      case 'time':
        return { primary: 'Response Time', secondary: 'Target Time', title: 'Response Time Analysis' };
      default:
        return { primary: 'Primary', secondary: 'Secondary', title: 'Overview' };
    }
  };

  useEffect(() => {
    setLoading(true);
    generateChartData();
  }, [activeChart, dashboardStats]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || data.length === 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    // Ensure canvas has valid dimensions
    if (rect.width === 0 || rect.height === 0) return;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Clear canvas and set background
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Set background color based on theme
    const isDarkMode = document.documentElement.classList.contains('dark');
    if (isDarkMode) {
      ctx.fillStyle = '#111827'; // dark:bg-gray-900
      ctx.fillRect(0, 0, rect.width, rect.height);
    } else {
      ctx.fillStyle = '#ffffff'; // bg-white
      ctx.fillRect(0, 0, rect.width, rect.height);
    }

    // Chart dimensions with proper padding to prevent overflow
    const padding = { top: 30, right: 30, bottom: 50, left: 60 };
    const chartWidth = Math.max(0, rect.width - padding.left - padding.right);
    const chartHeight = Math.max(0, rect.height - padding.top - padding.bottom);

    // Find max value for scaling
    const maxValue = Math.max(...data.map(d => Math.max(d.primary, d.secondary))) || 100;

    // Draw axes
    ctx.beginPath();
    ctx.strokeStyle = "#666";
    ctx.lineWidth = 0.5;

    // X-axis
    ctx.moveTo(padding.left, rect.height - padding.bottom);
    ctx.lineTo(rect.width - padding.right, rect.height - padding.bottom);

    // Y-axis
    ctx.moveTo(padding.left, padding.top);
    ctx.lineTo(padding.left, rect.height - padding.bottom);
    ctx.stroke();

    // Draw revenue area
    const drawArea = (values: number[], color: string, fillColor: string) => {
      ctx.beginPath();
      ctx.moveTo(padding.left, rect.height - padding.bottom);

      // Draw points and connect them
      data.forEach((d, i) => {
        const x = padding.left + (i * (chartWidth / (data.length - 1)));
        const y = padding.top + chartHeight - (values[i] / maxValue * chartHeight);
        ctx.lineTo(x, y);
      });

      // Complete the area
      ctx.lineTo(padding.left + chartWidth, rect.height - padding.bottom);
      ctx.closePath();

      // Fill area
      ctx.fillStyle = fillColor;
      ctx.fill();

      // Draw line
      ctx.beginPath();
      data.forEach((d, i) => {
        const x = padding.left + (i * (chartWidth / (data.length - 1)));
        const y = padding.top + chartHeight - (values[i] / maxValue * chartHeight);
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw points
      data.forEach((d, i) => {
        const x = padding.left + (i * (chartWidth / (data.length - 1)));
        const y = padding.top + chartHeight - (values[i] / maxValue * chartHeight);
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      });
    };

    // Draw primary and secondary data
    if (data.length > 0) {
      drawArea(data.map(d => d.primary), "#4B5563", "rgba(75, 85, 99, 0.1)");
      drawArea(data.map(d => d.secondary), "#9CA3AF", "rgba(156, 163, 175, 0.08)");
    }

    // Draw month labels
    ctx.textAlign = "center";
    ctx.fillStyle = "#666";
    ctx.font = "10px sans-serif";
    data.forEach((d, i) => {
      const x = padding.left + (i * (chartWidth / (data.length - 1)));
      ctx.fillText(d.month, x, rect.height - padding.bottom + 15);
    });

    // Draw value labels on y-axis
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    const yTicks = 5;
    for (let i = 0; i <= yTicks; i++) {
      const value = Math.round((maxValue / yTicks) * i);
      const y = rect.height - padding.bottom - (i / yTicks) * chartHeight;

      // Format value based on chart type
      let formattedValue = value.toString();
      if (activeChart === 'response') {
        formattedValue = `${value}%`;
      } else if (activeChart === 'rating') {
        formattedValue = `${(value / 20).toFixed(1)}★`;
      } else if (activeChart === 'time') {
        formattedValue = `${(value / 10).toFixed(1)}h`;
      }

      ctx.fillText(formattedValue, padding.left - 5, y);
    }

  }, [data, activeChart]);

  const labels = getChartLabels();

  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-gray-900">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{labels.title}</h3>
        </div>
        <div className="flex-1 min-h-[300px] w-full flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900 overflow-hidden">
      <div className="mb-6 flex items-center justify-between flex-shrink-0">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{labels.title}</h3>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded-full bg-gray-900 dark:bg-gray-100"></div>
            <span className="text-sm text-gray-500 dark:text-gray-400">{labels.primary}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded-full bg-gray-400 dark:bg-gray-600"></div>
            <span className="text-sm text-gray-500 dark:text-gray-400">{labels.secondary}</span>
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 w-full overflow-hidden">
        <canvas
          ref={canvasRef}
          className="h-full w-full max-w-full max-h-full"
          style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
        />
      </div>
    </div>
  );
}
